import React from 'react';

import { scrollToHash } from '../../hooks/useActiveSection';
import { fontHeading, linkClass } from './docsTypography';

export interface DocsTocLink {
  /** Hash including the leading `#`, e.g. `#getting-started`. */
  href: string;
  label: string;
}

interface DocsTableOfContentsProps {
  links: readonly DocsTocLink[];
  /** Anchor id of the section currently in view, or null if none. */
  activeId: string | null;
}

/**
 * Sticky left-rail "On this page" nav used by docs-style pages
 * (`Tutorial`, `BusinessApiDocumentation`). Renders the white card,
 * the "On this page" label, and the list of TOC links — highlighting
 * the entry whose `href` matches `#${activeId}` and wiring each link
 * through `scrollToHash` for smooth-scroll + history.pushState.
 *
 * The wrapping `<nav>` carries `aria-label="Table of contents"` so
 * that `useSiteNavHeight`'s selector can distinguish this nav from
 * the site chrome navbar — keep that label in sync if you rename it.
 */
export function DocsTableOfContents({
  links,
  activeId,
}: DocsTableOfContentsProps) {
  return (
    <nav
      // `lg:h-full` makes the desktop sidebar card span the full
      // scrollport height so it visually balances the article card on
      // the right. We intentionally do NOT set `overflow-y-auto` here:
      // the TOC is short (≤ a dozen entries) so it never overflows the
      // viewport, and leaving overflow as `visible` lets wheel events
      // over the empty area below the entries bubble up to the
      // scrollport and scroll the article — preserving the "no dead
      // zone" UX. If the TOC ever grows past the viewport height,
      // revisit this and add `lg:overflow-y-auto` plus accept that
      // wheel-over-TOC will no longer forward to the article.
      className={`
        rounded-lg border border-stone-200 bg-white p-4 shadow-sm
        lg:h-full
      `}
      aria-label="Table of contents"
    >
      <p
        className={`
          mb-3 text-xs font-semibold tracking-wide text-stone-500 uppercase
          ${fontHeading}
        `}
      >
        On this page
      </p>
      <ul className="list-none space-y-1.5 pl-0 text-[14px] text-stone-700">
        {links.map(({ href, label }) => {
          const isActive = href === `#${activeId}`;
          return (
            <li key={href}>
              <a
                href={href}
                onClick={(e) => scrollToHash(e, href)}
                aria-current={isActive ? 'true' : undefined}
                className={`
                  block py-0.5 transition-colors
                  ${isActive ? 'font-semibold text-stone-900' : `${linkClass}`}
                `}
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Convenience: derive the bare id list (`['getting-started', ...]`)
 * that `useActiveSection` expects from a `DocsTocLink[]`. Saves the
 * `links.map((l) => l.href.slice(1))` boilerplate in every consumer.
 */
export function getTocIds(links: readonly DocsTocLink[]): string[] {
  return links.map((l) => l.href.slice(1));
}
